import React, { useState } from 'react';
import { useRouter } from '../Router';
import { Link } from '../Link';
import { Logo } from '../Logo';
import SEO from '../SEO';
import { 
    Clock, ArrowRight, Database, 
    Bot, Target, Search, BookOpen, Terminal, Sparkles, ShieldCheck, CreditCard
} from 'lucide-react';

interface ArticleItem {
    id: string;
    title: string;
    excerpt: string;
    category: 'Nigeria Growth' | 'Integrations' | 'Sales Funnels' | 'AI Automation';
    readTime: string;
    publishedDate: string;
    href: string;
    metric: string;
    featured?: boolean;
    icon: React.ReactNode;
}

const ALL_ARTICLES: ArticleItem[] = [
    {
        id: 'whatsapp-automation-nigeria',
        title: "WhatsApp Automation in Nigeria: Complete Guide to Anti-Ban Meta API, AI Agents & Google Sheets Sync",
        excerpt: "The authoritative guide for Nigerian businesses scaling sales and support on WhatsApp. Learn official Meta Cloud API setup, Naira (₦) billing, automated Google Sheets order pipelines, and 24/7 Google Gemini AI chatbots.",
        category: 'Nigeria Growth',
        readTime: "8 min read",
        publishedDate: "Oct 2026",
        href: "/whatsapp-automation-nigeria",
        metric: "100% Anti-Ban (NGN)",
        featured: true,
        icon: <Sparkles className="w-4 h-4 text-emerald-400" />
    },
    {
        id: 'whatsapp-api-pricing-nigeria',
        title: "Official Meta WhatsApp API Pricing in Nigeria (2026 Naira Breakdown vs. Wati & Twilio)",
        excerpt: "Stop paying unpredictable USD card markups and per-message broker fees. Compare Naira (₦) subscription costs across Bytecohort Reach, Wati, and Twilio with our interactive NGN calculator.",
        category: 'Nigeria Growth',
        readTime: "8 min read",
        publishedDate: "Oct 2026",
        href: "/blog/whatsapp-api-pricing-nigeria",
        metric: "100% Naira Billing (₦)",
        featured: true,
        icon: <CreditCard className="w-4 h-4 text-emerald-400" />
    },
    {
        id: 'wasender-gbwhatsapp-ban-official-meta-api-nigeria',
        title: "Why Wasender & GBWhatsApp Get +234 Numbers Banned in Nigeria (And How to Migrate to Official Meta API)",
        excerpt: "Discover why unofficial browser clickers and modified APKs get Nigerian +234 business numbers banned, and how to migrate to the 100% anti-ban official Meta Cloud API in 5 minutes.",
        category: 'Nigeria Growth',
        readTime: "7 min read",
        publishedDate: "Oct 2026",
        href: "/blog/wasender-gbwhatsapp-ban-official-meta-api-nigeria",
        metric: "0.00% Ban Rate",
        featured: true,
        icon: <ShieldCheck className="w-4 h-4 text-amber-400" />
    },
    {
        id: 'paystack-whatsapp-order-automation-google-sheets-nigeria',
        title: "How to Automate WhatsApp Order Confirmations, Paystack Receipts & Delivery Alerts with Google Sheets in Nigeria",
        excerpt: "Step-by-step blueprint for Nigerian e-commerce stores, logistics companies, and real estate teams to automate WhatsApp order confirmations, +234 number normalization, and 24/7 Gemini AI support.",
        category: 'Integrations',
        readTime: "9 min read",
        publishedDate: "Oct 2026",
        href: "/blog/paystack-whatsapp-order-automation-google-sheets-nigeria",
        metric: "Auto +234 Sync",
        featured: true,
        icon: <Database className="w-4 h-4 text-cyan-400" />
    },
    {
        id: 'phone-lookup-sheets',
        title: "Zero-Drop WhatsApp Verification: How to Check Phone Numbers Against Google Sheets & Auto-Personalize Inbound Chats",
        excerpt: "Learn how modern engineering teams query spreadsheets in 280ms to verify customer records, pull dynamic variables (tier, balance), and prevent lead abandonment.",
        category: 'Integrations',
        readTime: "6 min read",
        publishedDate: "Sep 2026",
        href: "/blog/whatsapp-phone-verification-sheets",
        metric: "< 0.28s Sheet Query",
        featured: false,
        icon: <Database className="w-4 h-4 text-emerald-400" />
    },
    {
        id: 'sales-funnel',
        title: "WhatsApp Sales Funnel Architecture: Step-by-Step Blueprint to 4.8x Conversions",
        excerpt: "Replace low-open email campaigns with 98% open rate WhatsApp pipelines. Deploy autonomous qualification with the latest Gemini models and instant CRM updates.",
        category: 'Sales Funnels',
        readTime: "7 min read",
        publishedDate: "Aug 2026",
        href: "/blog/whatsapp-sales-funnel",
        metric: "+380% Response Rate",
        featured: false,
        icon: <Target className="w-4 h-4 text-cyan-400" />
    },
    {
        id: 'sheets-sync',
        title: "Automate Google Sheets WhatsApp Sync: Real-Time Bidirectional Workflow",
        excerpt: "Connect your spreadsheets directly to the Meta WhatsApp Cloud API. Automatically log incoming leads, update appointment statuses, and trigger bulk campaigns.",
        category: 'Integrations',
        readTime: "5 min read",
        publishedDate: "Aug 2026",
        href: "/blog/whatsapp-google-sheets-sync",
        metric: "0 Manual Entry",
        icon: <Database className="w-4 h-4 text-emerald-400" />
    },
    {
        id: 'qualify-leads',
        title: "How to Qualify Inbound WhatsApp Leads with Autonomous AI Agents Without Human Fatigue",
        excerpt: "Frameworks and guardrails using the latest Gemini models to score customer intent, parse order numbers, and seamlessly hand off warm leads to human reps.",
        category: 'AI Automation',
        readTime: "6 min read",
        publishedDate: "Jul 2026",
        href: "/blog/qualify-whatsapp-leads",
        metric: "< 45s Triage Time",
        icon: <Bot className="w-4 h-4 text-indigo-400" />
    }
];

export const BlogIndex: React.FC = () => {
    const { navigate } = useRouter();
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [searchQuery, setSearchQuery] = useState<string>('');

    const categories = ['All', 'Nigeria Growth', 'Integrations', 'Sales Funnels', 'AI Automation'];

    const filteredArticles = ALL_ARTICLES.filter(art => {
        const matchesCategory = selectedCategory === 'All' || art.category === selectedCategory;
        const matchesSearch = searchQuery.trim() === '' || 
            art.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
            art.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    return (
        <div className="min-h-screen bg-[#07080B] text-zinc-100 font-sans antialiased selection:bg-emerald-500 selection:text-black flex flex-col relative overflow-x-hidden">
            <SEO
                title="WhatsApp Automation Playbooks & Nigeria Guides | Bytecohort Reach Blog"
                description="Actionable guides, Naira (₦) pricing breakdowns, official Meta Cloud API anti-ban tutorials, and Google Sheets WhatsApp automation blueprints for Nigerian and global businesses."
                keywords="WhatsApp Automation Blog Nigeria, Meta WhatsApp Cloud API Guide Nigeria, WhatsApp API Pricing Naira, Google Sheets WhatsApp Sync Tutorial, Wasender Alternative Nigeria"
                canonical="https://bytecohortreach.com/blog"
            />

            {/* Atmospheric Ambient Light */}
            <div className="fixed inset-0 pointer-events-none z-0">
                <div className="absolute top-0 right-1/4 w-[600px] h-[350px] bg-emerald-500/[0.03] blur-[150px] rounded-full pointer-events-none" />
                <div className="absolute bottom-1/3 left-0 w-[500px] h-[500px] bg-cyan-500/[0.02] blur-[160px] rounded-full pointer-events-none" />
            </div>

            {/* Live Operational Status Bar */}
            <div className="relative z-50 w-full bg-[#0B0D14] border-b border-white/[0.06] py-1.5 px-4 text-[11px] font-mono text-zinc-400">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1.5 text-emerald-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            Official Meta Graph API v21.0 Connected
                        </span>
                        <span className="hidden sm:inline text-zinc-600">•</span>
                        <span className="hidden sm:inline">Nigeria & Global Playbooks</span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-mono">
                        <Link href="/whatsapp-automation-nigeria" className="text-emerald-400 hover:underline">
                            Nigeria Pillar Hub 🇳🇬
                        </Link>
                        <span className="text-zinc-600">•</span>
                        <a href="/rss.xml" className="text-zinc-400 hover:text-white">RSS Feed</a>
                    </div>
                </div>
            </div>

            {/* Architectural Navigation Header */}
            <header className="sticky top-0 z-40 bg-[#07080B]/85 backdrop-blur-md border-b border-white/[0.06]">
                <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-8">
                        <Link href="/" className="flex items-center space-x-2">
                            <Logo className="h-8 w-auto" />
                        </Link>
                        <div className="hidden md:flex items-center gap-6 text-xs font-medium text-zinc-400">
                            <Link href="/" className="hover:text-white transition-colors">Platform</Link>
                            <Link href="/whatsapp-automation-nigeria" className="text-emerald-400 hover:text-emerald-300 transition-colors font-semibold">
                                Nigeria Hub 🇳🇬
                            </Link>
                            <Link href="/pricing" className="hover:text-white transition-colors">Pricing (₦)</Link>
                            <Link href="/docs" className="hover:text-white transition-colors">Docs</Link>
                            <Link href="/blog" className="text-white font-semibold flex items-center gap-1">
                                Playbooks
                                <span className="w-1 h-1 rounded-full bg-emerald-400" />
                            </Link>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <Link 
                            href="/app" 
                            className="text-xs font-medium text-zinc-300 hover:text-white px-3 py-1.5 rounded-lg transition-colors"
                        >
                            Sign In
                        </Link>
                        <Link 
                            href="/app" 
                            className="text-xs font-semibold text-zinc-950 bg-white hover:bg-zinc-200 px-4 py-2 rounded-lg transition-all shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2)] flex items-center gap-1.5"
                        >
                            <span>Launch Console</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                </nav>
            </header>

            {/* Hero Section */}
            <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-12 w-full">
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-mono uppercase tracking-wider mb-6">
                        <BookOpen className="w-3.5 h-3.5" />
                        WhatsApp Automation Nigeria & Engineering Blueprints
                    </div>
                    
                    <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-[1.15] mb-4">
                        The WhatsApp Infrastructure & Nigeria Growth Playbook
                    </h1>
                    
                    <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-2xl font-normal">
                        Step-by-step guides on official Meta WhatsApp Cloud API setup in Nigeria, Naira (₦) pricing via Paystack, anti-ban compliance, two-way Google Sheets order sync, and autonomous Gemini AI agents.
                    </p>
                </div>

                {/* Filter and Search Bar */}
                <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-white/[0.06]">
                    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={`px-3.5 py-1.5 rounded-lg text-xs font-mono transition-all ${
                                    selectedCategory === cat
                                        ? 'bg-white text-zinc-950 font-bold shadow-sm'
                                        : 'bg-[#0B0D14] text-zinc-400 hover:text-white border border-white/[0.08]'
                                }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full sm:w-80">
                        <Search className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search Nigeria guides, Naira pricing, Sheets..."
                            className="w-full bg-[#0B0D14] border border-white/[0.08] rounded-xl pl-10 pr-4 py-2 text-xs text-white placeholder-zinc-500 focus:border-emerald-500/50 focus:outline-none transition-colors"
                        />
                    </div>
                </div>
            </section>

            {/* Articles Grid - Rendered with Crawlable <Link> Anchor Tags */}
            <main className="flex-1 relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-24 w-full">
                {filteredArticles.length === 0 ? (
                    <div className="p-16 text-center border border-white/[0.08] bg-[#0B0D14] rounded-2xl">
                        <p className="text-zinc-400 text-sm mb-2">No playbooks matched your filter criteria.</p>
                        <button
                            onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}
                            className="text-xs text-emerald-400 hover:underline font-mono"
                        >
                            Reset filters
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                        {filteredArticles.map((art) => (
                            <Link
                                key={art.id}
                                href={art.href}
                                className="group relative rounded-2xl p-7 flex flex-col justify-between transition-all duration-300 bg-[#0B0D14] border border-white/[0.08] hover:border-emerald-500/40 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] cursor-pointer block"
                            >
                                {/* Precision Architectural Corner Crosshairs */}
                                <div className="absolute -top-1.5 -left-1.5 font-mono text-[10px] text-zinc-600 pointer-events-none select-none">+</div>
                                <div className="absolute -top-1.5 -right-1.5 font-mono text-[10px] text-zinc-600 pointer-events-none select-none">+</div>
                                <div className="absolute -bottom-1.5 -left-1.5 font-mono text-[10px] text-zinc-600 pointer-events-none select-none">+</div>
                                <div className="absolute -bottom-1.5 -right-1.5 font-mono text-[10px] text-zinc-600 pointer-events-none select-none">+</div>

                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-white/[0.04] text-zinc-300 border border-white/[0.08]">
                                                {art.category}
                                            </span>
                                            {art.featured && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-400">
                                                    <Sparkles className="w-3 h-3" />
                                                    Featured
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-[11px] text-zinc-500 font-mono flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {art.readTime}
                                        </span>
                                    </div>

                                    <div className="p-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] w-fit mb-4 group-hover:border-emerald-500/30 transition-colors">
                                        {art.icon}
                                    </div>

                                    <h2 className="text-lg sm:text-xl font-bold text-white group-hover:text-emerald-400 transition-colors tracking-tight leading-snug mb-3">
                                        {art.title}
                                    </h2>

                                    <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed font-normal line-clamp-3 mb-6">
                                        {art.excerpt}
                                    </p>
                                </div>

                                <div className="pt-4 border-t border-white/[0.06] flex items-center justify-between mt-auto">
                                    <span className="text-[11px] font-mono text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-md border border-emerald-500/20 font-medium">
                                        {art.metric}
                                    </span>
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-300 group-hover:text-white transition-colors">
                                        <span>Read Playbook</span>
                                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}

                {/* Architectural Conversion CTA Card */}
                <div className="mt-20 p-8 sm:p-12 rounded-2xl bg-[#0B0D14] border border-white/[0.08] relative overflow-hidden shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/[0.04] rounded-full blur-[120px] pointer-events-none" />
                    
                    <div className="relative z-10 max-w-2xl">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-mono uppercase tracking-wider mb-4">
                            <Terminal className="w-3.5 h-3.5" />
                            Production Readiness • Nigeria & Global
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-black text-white mb-3 tracking-tight">
                            Ready to deploy anti-ban WhatsApp automation in Nigeria?
                        </h2>
                        <p className="text-xs sm:text-sm text-zinc-400 mb-8 leading-relaxed">
                            Connect your +234 number and Google Sheet in minutes. Build visual autonomous routing driven by the latest Gemini models on the official Meta Cloud Graph API. Pay in Naira (₦).
                        </p>

                        <div className="flex flex-col sm:flex-row items-center gap-3">
                            <button
                                onClick={() => navigate('/app')}
                                className="w-full sm:w-auto bg-white hover:bg-zinc-200 text-zinc-950 text-xs font-semibold uppercase tracking-wider px-6 py-3 rounded-xl transition-all shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2)] flex items-center justify-center gap-2"
                            >
                                <span>Launch Workspace Console</span>
                                <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                            <Link
                                href="/whatsapp-automation-nigeria"
                                className="w-full sm:w-auto bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-semibold px-6 py-3 rounded-xl border border-emerald-500/30 transition-colors flex items-center justify-center gap-2 font-mono"
                            >
                                <span>Explore Nigeria Pillar Guide 🇳🇬</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </main>

            {/* Unified Architectural Footer */}
            <footer className="border-t border-white/[0.08] bg-[#07080B] relative z-10 pt-16 pb-12 mt-auto">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <Logo className="h-6 w-auto" />
                            </div>
                            <p className="text-xs text-zinc-400 leading-relaxed mb-4">
                                Official Meta Cloud API infrastructure for Nigerian and global businesses with autonomous Gemini AI agents and real-time Google Sheets sync.
                            </p>
                            <div className="flex items-center gap-2 text-[10px] font-mono text-emerald-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                Meta Tech Provider Partner
                            </div>
                        </div>

                        <div>
                            <p className="text-[11px] font-mono font-semibold uppercase tracking-wider text-zinc-300 mb-4">Nigeria Hub</p>
                            <ul className="space-y-2 text-xs text-zinc-400">
                                <li><Link href="/whatsapp-automation-nigeria" className="text-emerald-400 hover:underline font-medium">WhatsApp Automation Nigeria 🇳🇬</Link></li>
                                <li><Link href="/blog/whatsapp-api-pricing-nigeria" className="hover:text-white transition-colors">WhatsApp API Pricing in Naira</Link></li>
                                <li><Link href="/blog/wasender-gbwhatsapp-ban-official-meta-api-nigeria" className="hover:text-white transition-colors">Stop +234 Number Bans</Link></li>
                                <li><Link href="/blog/paystack-whatsapp-order-automation-google-sheets-nigeria" className="hover:text-white transition-colors">Paystack & Sheets Order Sync</Link></li>
                            </ul>
                        </div>

                        <div>
                            <p className="text-[11px] font-mono font-semibold uppercase tracking-wider text-zinc-300 mb-4">Engineering Playbooks</p>
                            <ul className="space-y-2 text-xs text-zinc-400">
                                <li><Link href="/blog/whatsapp-phone-verification-sheets" className="hover:text-white transition-colors">Sheets Phone Verification</Link></li>
                                <li><Link href="/blog/whatsapp-sales-funnel" className="hover:text-white transition-colors">WhatsApp Sales Funnel</Link></li>
                                <li><Link href="/blog/whatsapp-google-sheets-sync" className="hover:text-white transition-colors">Bidirectional Sync</Link></li>
                                <li><Link href="/blog/qualify-whatsapp-leads" className="hover:text-white transition-colors">Autonomous AI Qualification</Link></li>
                            </ul>
                        </div>

                        <div>
                            <p className="text-[11px] font-mono font-semibold uppercase tracking-wider text-zinc-300 mb-4">Platform & Trust</p>
                            <ul className="space-y-2 text-xs text-zinc-400">
                                <li><Link href="/" className="hover:text-white transition-colors">Platform Overview</Link></li>
                                <li><Link href="/pricing" className="hover:text-white transition-colors">Naira Pricing Plans</Link></li>
                                <li><Link href="/docs" className="hover:text-white transition-colors">Developer Docs</Link></li>
                                <li><Link href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                            </ul>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-zinc-500 font-mono">
                        <p>&copy; {new Date().getFullYear()} Bytecohort Reach. All rights reserved.</p>
                        <p>Meta WhatsApp Business Solution Architecture • Lagos, Nigeria & Global</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default BlogIndex;
